import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExportButton } from '@/components/common/ExportButton';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { RunFooter } from '@/pages/anatel/conselho/RunFooter';
import { ApiError } from '@/lib/api';
import { fetchAnatelConselhoCases } from '@/services/anatel';
import type { ConselhoScopeCase } from '@/services/types';

/**
 * The acórdãos behind ONE severity figure on a member's page (no orphan
 * numbers): every case the run counted in that row's n, the favorable share
 * among them, and the same share over every relator in the same scope.
 *
 * Reached by clicking a scope row on /anatel/r/:slug, never from the menu.
 * The API answers only when its list holds exactly the n the run wrote, so a
 * count on this page and the figure it came from cannot disagree.
 */
export function AnatelConselhoCasesPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const [params] = useSearchParams();
  const member = params.get('member') ?? '';
  const scope = params.get('scope') ?? 'overall';
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'conselho', 'cases', member, scope],
    queryFn: () => fetchAnatelConselhoCases(member, scope),
    enabled: member !== '',
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (member === '' || (error instanceof ApiError && error.status === 404)) {
    return (
      <div className="p-6">
        <ErrorMessage message={t('anatel:conselheiro.notFound')} />
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={8} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const signed = new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 });
  const share = (favorable: number, total: number) =>
    t('anatel:conselho.cases.share', {
      favorable,
      total,
      share: total > 0 ? percent.format(favorable / total) : '—',
    });
  const m = data.metric;

  return (
    <div className="space-y-4 p-6">
      <Link
        to={`/anatel/r/${data.slug}`}
        className="inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        {t('anatel:conselho.cases.back')}
      </Link>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:conselho.cases.title')}</h1>
        <p className="text-sm text-foreground" data-testid="cases-subject">
          {data.name} — {data.scope}
        </p>
        <p className="text-sm text-muted-foreground">{t('anatel:conselho.cases.subtitle')}</p>
      </header>

      <div className="flex justify-end">
        <ExportButton path={`/api/v1/anatel/export/conselho/cases?${new URLSearchParams({ member, scope })}`} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <Answer answer={data.cases} scope="record">
        {(value) => (
          <div className="space-y-4">
            <section className="rounded-lg border bg-card p-4" data-panel="cases-figure">
              <h2 className="mb-2 text-sm font-semibold text-foreground">{t('anatel:conselho.cases.figure')}</h2>
              <dl className="grid gap-x-4 gap-y-2 text-xs sm:grid-cols-3">
                {m && m.adjusted_effect !== null && m.lower !== null && m.upper !== null ? (
                  <div data-testid="cases-adjusted">
                    <dt className="text-muted-foreground">{t('anatel:conselho.cases.adjusted')}</dt>
                    <dd className="tabular-nums text-foreground">
                      {signed.format(m.adjusted_effect)}{' '}
                      <span className="text-muted-foreground">
                        [{signed.format(m.lower)}, {signed.format(m.upper)}]
                      </span>
                    </dd>
                  </div>
                ) : null}
                <div data-testid="cases-raw">
                  <dt className="text-muted-foreground">{t('anatel:conselho.cases.raw')}</dt>
                  <dd className="tabular-nums text-foreground">{share(value.favorable, value.cases.length)}</dd>
                </div>
                <div data-testid="cases-bench">
                  <dt className="text-muted-foreground">{t('anatel:conselho.cases.bench')}</dt>
                  <dd className="tabular-nums text-foreground">{share(value.bench_favorable, value.bench_cases)}</dd>
                </div>
              </dl>
              <p className="mt-2 text-[11px] text-muted-foreground">{t('anatel:conselho.cases.readTogether')}</p>
              <RunFooter run={data.run} />
            </section>
            <CasesTable cases={value.cases} />
          </div>
        )}
      </Answer>
    </div>
  );
}

function CasesTable({ cases }: { cases: ConselhoScopeCase[] }) {
  const { t } = useTranslation(['anatel']);
  return (
    <div className="max-h-[32rem] overflow-auto rounded-lg border bg-card">
      <table className="w-full text-xs" data-testid="cases-table">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            <th className="p-2">{t('anatel:conselho.cases.date')}</th>
            <th className="p-2">{t('anatel:conselho.cases.acordao')}</th>
            <th className="p-2">{t('anatel:conselho.cases.process')}</th>
            <th className="p-2">{t('anatel:conselho.cases.tematica')}</th>
            <th className="p-2">{t('anatel:conselho.cases.partyClass')}</th>
            <th className="p-2">{t('anatel:conselho.cases.direction')}</th>
            <th className="p-2">{t('anatel:conselho.cases.unanimity')}</th>
          </tr>
        </thead>
        <tbody>
          {cases.map((c, i) => (
            // Server-ordered and never re-sorted here, and one process can
            // carry several acórdãos: the index is the stable key.
            <tr key={i} className="border-t" data-direction={c.direction}>
              <td className="p-2 tabular-nums">{c.date ?? '—'}</td>
              <td className="p-2 tabular-nums">{c.acordao ?? '—'}</td>
              <td className="p-2 tabular-nums">{c.process_number ?? '—'}</td>
              <td className="p-2">{c.tematica ?? '—'}</td>
              <td className="p-2 text-muted-foreground">{c.party_class ?? '—'}</td>
              <td className="p-2">{t(`anatel:conselho.cases.directions.${c.direction}`, c.direction)}</td>
              <td className="p-2 text-muted-foreground">{c.unanimity ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
